import type { ReactNode } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { Text, type TextVariants } from '@/components/text';
import type { Asset, Entry, Maybe } from '@/app/generated/graphql';
import type {
  Document,
  Block,
  Inline,
  Node,
} from '@contentful/rich-text-types';

import { CustomLink } from '../custom-link';

type Links = {
  assets: {
    block: Maybe<Asset>[];
    hyperlink: Maybe<Asset>[];
  };
  entries: {
    block: Maybe<Entry>[];
    hyperlink: Maybe<Entry>[];
    inline: Maybe<Entry>[];
  };
};

type OptionProps = {
  uri?: string;
  bodyVariant?: TextVariants['variant'];
  bodySpacing?: TextVariants['spacing'];
  links?: Links;
};

type RichTextProps = {
  document: Document;
} & OptionProps;

// For more node and mark options: https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
const options = ({
  links,
  bodyVariant,
  bodySpacing = 'paragraph',
}: OptionProps) => {
  // Create maps to render linked assets
  // https://www.contentful.com/blog/rendering-linked-assets-entries-in-contentful/
  const assetBlockMap = new Map<string, Asset>();
  const entryBlockMap = new Map<string, Entry>();

  if (links) {
    for (const asset of links.assets.block) {
      if (!asset) return;
      assetBlockMap.set(asset.sys.id, asset);
    }

    for (const asset of links.assets.hyperlink) {
      if (!asset) return;
      assetBlockMap.set(asset.sys.id, asset);
    }

    for (const entry of links.entries.block) {
      if (!entry) return;

      entryBlockMap.set(entry.sys.id, entry);
    }
  }

  return {
    preserveWhitespace: true,
    renderMark: {
      [MARKS.BOLD]: (text) => <b>{text}</b>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <Text
          variant={bodyVariant}
          spacing={bodySpacing}
          as="p"
          className="last:mb-0"
        >
          {children}
        </Text>
      ),
      [BLOCKS.TABLE]: (node, children) => (
        <table className="w-full border-collapse border border-black">
          {children}
        </table>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td className="border border-black p-a">{children}</td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className="border border-black p-a">{children}</th>
      ),
      [BLOCKS.HEADING_1]: (node, children) => (
        <Text
          variant="h1"
          as="h1"
          className="mb-b"
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <Text
          variant="h2"
          as="h2"
          className="mb-b"
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <Text
          variant="h3"
          as="h3"
          className="mb-b"
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <Text
          variant="h4"
          as="h4"
          className="mb-b"
        >
          {children}
        </Text>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="list-disc pl-4">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="list-decimal pl-4">{children}</ol>
      ),
      [INLINES.HYPERLINK]: (node: Node, children) => {
        return (
          <CustomLink
            href={node.data.uri as string}
            isRichText
          >
            {children}
          </CustomLink>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node: Block | Inline, children) => {
        const assetHyperlink = assetBlockMap.get(`${node.data.target.sys.id}`);

        return (
          <CustomLink
            href={assetHyperlink?.url || ''}
            download
            target="_blank"
          >
            {children}
          </CustomLink>
        );
      },
    },
  };
};

export const RichText = ({
  document,
  links,
  ...props
}: RichTextProps): ReactNode => {
  return documentToReactComponents(document, options({ links, ...props }));
};
